import { Loader2 } from "lucide-react";
import type { ReactNode } from "react";

import { Alert, AlertDescription, cn, Skeleton, Text } from "@lagoon/ui";

import { ApiError } from "../api.js";

/**
 * The three shapes every screen in this application needs and the design
 * library deliberately does not ship: something is loading, something failed,
 * and a panel is still arriving.
 *
 * They are compositions of library atoms rather than new styling. Nothing here
 * declares a colour, a type size or a spacing value of its own; every class is
 * a token-backed utility from the preset. That is the line: the dashboard may
 * compose the library, and may not restate it.
 */

/**
 * The in-flight indicator.
 *
 * Decorative, so it is hidden from assistive technology and the sentence
 * beside it does the announcing. `animate-spin` collapses under
 * `prefers-reduced-motion`, because the library's base stylesheet flattens
 * every animation under that query rather than only the three the brand's own
 * page bothered with.
 */
function Spinner(props: { className?: string }): React.ReactElement {
  return (
    <Loader2
      aria-hidden="true"
      className={cn("lg:size-4 lg:shrink-0 lg:animate-spin lg:text-primary", props.className)}
    />
  );
}

/** The spinner with its sentence: "Loading…", "Uploading to storage…". */
export function Working(props: { label: string; className?: string }): React.ReactElement {
  return (
    <span className={cn("lg:inline-flex lg:items-center lg:gap-2", props.className)}>
      <Spinner />
      <Text as="span" size="caption" tone="subtle">
        {props.label}
      </Text>
    </span>
  );
}

/**
 * A panel that has not arrived.
 *
 * Skeleton lines rather than a spinner, because this replaces a card that is
 * about to be several rows tall and a centred spinner in that space makes the
 * layout jump when the data lands.
 */
export function LoadingPanel(props: { rows?: number; label?: string }): React.ReactElement {
  const rows = props.rows ?? 3;
  return (
    <div
      className="lg:rounded-lg lg:border lg:border-hairline lg:bg-card lg:px-[1.6rem] lg:py-[1.7rem]"
      aria-busy="true"
    >
      <span className="lg:sr-only">{props.label ?? "Loading"}</span>
      <div className="lg:flex lg:flex-col lg:gap-3">
        {Array.from({ length: rows }, (_unused, index) => (
          <Skeleton
            key={`row-${String(index)}`}
            className={cn("lg:h-4", index === 0 ? "lg:w-1/3" : "lg:w-full")}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * A refusal, in the words the platform chose.
 *
 * `ApiError` carries a sentence written for a person; anything else is a
 * network fault or a bug, and the caller's fallback is what says so. Matching
 * on the type rather than on the text is what lets the platform reword its
 * messages without this file noticing.
 */
export function Failure(props: {
  error: unknown;
  fallback: string;
  children?: ReactNode;
}): React.ReactElement {
  return (
    <Alert tone="danger">
      <AlertDescription>
        {props.children}
        {props.error instanceof ApiError ? props.error.message : props.fallback}
      </AlertDescription>
    </Alert>
  );
}

/** A one-sentence notice in a tone. Saves repeating the `Alert` pair. */
export function Notice(props: {
  tone: "neutral" | "success" | "warning" | "danger" | "info";
  children: ReactNode;
  className?: string;
}): React.ReactElement {
  return (
    <Alert tone={props.tone} className={props.className}>
      <AlertDescription>{props.children}</AlertDescription>
    </Alert>
  );
}
